'use strict'

import AppUrlBuilder from '../components/urlBuilder/AppsUrlBuilder/Builder.vue'
import CreateProjectModal from '../components/projects/CreateProjectModal.vue'

import { ModalProgrammatic } from '@akerolabs/akero-vue/src/components/modal/index.js'
import { DialogProgrammatic } from '@akerolabs/akero-vue/src/components/dialog/index.js'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src/components/toast/index.js'
import { SnackbarProgrammatic } from '@akerolabs/akero-vue/src/components/snackbar/index.js'
import { NotificationProgrammatic } from '@akerolabs/akero-vue/src/components/notification/index.js'
import { MediaManagerProgrammatic } from '@akerolabs/akero-vue/src/components/mediaManager/index.js'
import { VariablesProgrammatic } from '@akerolabs/akero-vue/src/components/variables/index.js'
import * as agencyHelpers from '@akerolabs/akero-vue/src/services/agency.js'
import * as userHelpers from '@akerolabs/akero-vue/src/services/users.js'
import * as accountHelpers from '@akerolabs/akero-vue/src/services/accounts.js'
import * as appHelpers from '@akerolabs/akero-vue/src/services/apps.js'
import * as featureHelpers from '@akerolabs/akero-vue/src/services/features.js'
import * as helpers from '@akerolabs/akero-vue/src/utils/helpers.js'
import i18n from '@akerolabs/akero-vue/src/utils/i18n.js'

import {
  setHandler,
  isDemo,
} from '@akerolabs/akero-vue/src/services/demoMode.js'

/**
 * @returns {Function}
 * @description Constructor to create the "vueProgrammatic" angular service
 */
export default function vueProgrammaticConstructor() {
  /**
   * @returns {Object}
   * @description Returns the "vueProgrammatic" service which will allow
   * the use of the Vue programmatic components in Angular i.e modals.
   */
  return function vueProgrammatic() {
    /**
     * @type {Object}
     * @description Here we are going to expose the public API for
     * interacting with the programmatic vue components.
     */
    return {
      /**
       * @type {Object} expose the setLang function to angular so it can set
       * the users locale when they login
       */
      i18n: {
        setLang(lang = 'en') {
          i18n.global.locale = lang
        },

        getLang() {
          return i18n.global.locale
        },
      },

      /**
       * @type {Object}
       * @description This will expose the open method from the Vue
       * components for the "dialog" component library
       */
      dialog: {
        /**
         * @param  {Object} params
         * @description Opens an alert dialog
         */
        alert(params) {
          DialogProgrammatic.alert(params)
        },

        /**
         * @param  {Object} params
         * @description Opens a confirmation dialog
         */
        confirm(params) {
          DialogProgrammatic.confirm(params)
        },

        /**
         * @param  {Object} params
         * @description Opens a prompt dialog
         */
        prompt(params) {
          DialogProgrammatic.prompt(params)
        },
      },

      /**
       * @type {Object}
       * @description This will expose the open method from the Vue
       * components for the "modal" component library
       */
      modal: {
        /**
         * @param  {Object} params
         * @description Opens a modal window
         */
        open(params) {
          ModalProgrammatic.open(params)
        },
      },

      /**
       * @type {Object}
       * @description This will expose the open method from the Vue
       * components for the "toast" component library
       */
      toast: {
        /**
         * @param  {Object} params
         * @description Creates a toast notification
         */
        open(params) {
          ToastProgrammatic.open(params)
        },
      },

      /**
       * @type {Object}
       * @description This will expose the open method from the Vue
       * components for the "snackbar" component library
       */
      snackbar: {
        /**
         * @param  {Object} params
         * @description Creates a snackbar notification
         */
        open(params) {
          SnackbarProgrammatic.open(params)
        },
      },

      /**
       * @type {Object}
       * @description This will expose the open method from the Vue
       * components for the "notification" component library
       */
      notification: {
        /**
         * @param  {Object} params
         * @description Creates a notification
         */
        open(params) {
          NotificationProgrammatic.open(params)
        },
      },

      /**
       * @type {Object}
       * @description This will expose the open method from the Vue
       * components for the "mediaManager" component library
       */
      mediaManager: {
        /**
         * @param  {Object} params
         * @description Opens the mediaManager
         */
        open(params) {
          MediaManagerProgrammatic.open(params)
        },
      },

      /**
       * @type {Object}
       * @description This will expose the url builder to the angular
       * application
       */
      urlBuilder: {
        /**
         * @param {Object} app - the app the make a url for
         * @description Opens the URL builder modal window
         */
        open(app) {
          ModalProgrammatic.open({
            component: AppUrlBuilder,
            outerScroll: true,
            size: 'large',
            props: {
              modelValue: app,
            },
          })
        },
      },

      /**
       * @type {Object}
       * @description This will expose project related functions to the admin panel
       */
      projects: {
        /**
         * @param {Object} options
         * @description Opens the create project modal window
         */
        create(options) {
          ModalProgrammatic.open({
            component: CreateProjectModal,
            outerScroll: true,
            size: 'large',
            submitForm: options.callback,
            props: options.props,
          })
        },
      },

      /**
       * @type {Object}
       * @description This will expose the open method from the Vue
       * components for the "variables" component library
       */
      variables: {
        /**
         * @param  {Object} params
         * @description Opens the variables modal
         */
        open(params) {
          VariablesProgrammatic.open(params)
        },
      },

      /**
       * @type {Object}
       * @description Here we are going to expose a couple of methods to expose
       * the applications state so that we are able to read the information from
       * any part of the vue application.
       */
      demoMode: {
        /**
         * @type {Function}
         * @returns {Boolean}
         * @description Checks if the demo mode is enabled
         */
        enabled() {
          return isDemo()
        },

        /**
         * @type {Function}
         * @param {Function} fn
         * @description Sets the handler method to check if the demo mode
         * is enabled.
         */
        setHandler(fn) {
          setHandler(fn)
        },
      },

      /**
       * @type {Object}
       * @description A bunch of cloudinary helper functions
       */
      cloudinary: {
        options: helpers.cloudinaryOptions,
      },

      /**
       * @type {Object}
       * @description A bunch of helpers to check for feature access
       */
      features: {
        ...featureHelpers,
      },

      /**
       * @type {Object}
       * @description A bunch of helpful functions that are used through out
       * the system, a lot of these are for accounts/agencies/users ect.
       */
      helpers: {
        ...agencyHelpers,
        ...accountHelpers,
        ...userHelpers,
        ...appHelpers,
      },

      /**
       * @type {Object}
       * @description A bunch of utility methods
       */
      utils: {
        ...helpers,
      },
    }
  }
}
